import type {
	InfiniteHitsCache,
	InfiniteHitsCachedHits
} from "instantsearch.js/es/connectors/infinite-hits/connectInfiniteHits";
import type { Hit } from "instantsearch.js/es/types";

import {
	LISTING_PAGE_PARAM,
	MAX_LISTING_PAGES,
	parseListingPages
} from "@/domain/product-listing/listing-depth";

import { LISTING_INDEX } from "../listing-search";

type CachedPages<THit extends object> = InfiniteHitsCachedHits<THit>;
type SearchState = Parameters<InfiniteHitsCache["read"]>[0]["state"];

// Enough for Back through a few filter and sort changes; bounded so the tab's storage stays small.
const STATES_KEPT = 5;

export interface ServerPages<THit extends object> {
	/** The page index the first of `pages` is. */
	first: number;
	pages: Hit<THit>[][];
	/** Whether the server already rendered every product in the listing. */
	complete: boolean;
}

// Drops empty values and orders keys, so a filter added then removed reads as never added.
function normalized(value: unknown): unknown {
	if (Array.isArray(value)) {
		const values = value
			.map(normalized)
			.filter((entry) => entry !== undefined);
		return values.length > 0 ? values : undefined;
	}
	if (value && typeof value === "object") {
		const entries = Object.entries(value)
			.map(([key, entry]) => [key, normalized(entry)] as const)
			.filter(([, entry]) => entry !== undefined)
			.sort(([a], [b]) => (a < b ? -1 : 1));
		return entries.length > 0 ? Object.fromEntries(entries) : undefined;
	}
	return value === "" ? undefined : value;
}

function stateKey(state: SearchState): string {
	return JSON.stringify(normalized({ ...state, page: undefined })) ?? "";
}

/** The listing the server renders: no filters, the default sort. */
export function isPlainListing(state: SearchState): boolean {
	return (
		(state.index ?? LISTING_INDEX) === LISTING_INDEX &&
		normalized([
			state.query,
			state.facetsRefinements,
			state.disjunctiveFacetsRefinements,
			state.numericRefinements,
			state.hierarchicalFacetsRefinements,
			state.tagRefinements
		]) === undefined
	);
}

/**
 * The run of loaded pages shown while `page` is searched and the url names
 * `urlPage`. Null when they stop short of the page before it, because only
 * `page` itself is searched. Show previous searches the page before the run
 * and leaves the url alone, so the run stays; Back to a shallower url shows
 * fewer; at the deepest page every page loaded past it stays.
 */
export function pagesUpTo<THit extends object>(
	cached: CachedPages<THit>,
	page: number,
	urlPage = page
): CachedPages<THit> | null {
	const indexes = Object.keys(cached)
		.map(Number)
		.filter((index) => cached[index]);
	if (indexes.length === 0) return null;
	const first = Math.min(...indexes);
	let loaded = first;
	while (cached[loaded + 1]) loaded++;
	if (page < first - 1 || loaded < page - 1) return null;

	const upTo = Math.max(page, urlPage);
	const last =
		upTo === MAX_LISTING_PAGES - 1 ? loaded : Math.min(loaded, upTo);
	const pages: CachedPages<THit> = {};
	for (let index = first; index <= last; index++) {
		pages[index] = cached[index];
	}
	return pages;
}

function currentUrlPage(): number {
	if (typeof window === "undefined") return 0;
	const pages = parseListingPages(
		new URLSearchParams(window.location.search).get(LISTING_PAGE_PARAM)
	);
	return (pages ?? 1) - 1;
}

interface StoredState<THit extends object> {
	state: string;
	pages: CachedPages<THit>;
}

function readStored<THit extends object>(key: string): StoredState<THit>[] {
	try {
		return JSON.parse(window.sessionStorage.getItem(key) ?? "[]");
	} catch {
		return [];
	}
}

function writeStored<THit extends object>(
	key: string,
	stored: StoredState<THit>[]
): void {
	try {
		window.sessionStorage.setItem(key, JSON.stringify(stored));
	} catch {
		// A full or blocked storage only costs restoring the place on Back.
	}
}

/**
 * Starts from the products the server rendered, so a deeper url is not
 * searched again, and keeps what the shopper loaded for each filter and sort
 * in this tab, so Back restores it.
 */
export function createListingHitsCache<THit extends object>(
	key: string,
	server: ServerPages<THit>
): InfiniteHitsCache<THit> {
	const serverPages: CachedPages<THit> = Object.fromEntries(
		server.pages.map((hits, index) => [server.first + index, hits])
	);

	function fromServer(
		page: number,
		urlPage: number
	): CachedPages<THit> | null {
		return (
			pagesUpTo(serverPages, page, urlPage) ??
			(server.complete && server.pages.length > 0
				? { ...serverPages }
				: null)
		);
	}

	return {
		read({ state }) {
			const page = state.page ?? 0;
			const urlPage = currentUrlPage();
			const stored = readStored<THit>(key).find(
				(entry) => entry.state === stateKey(state)
			);
			const plain = isPlainListing(state);
			const loaded =
				stored &&
				pagesUpTo(
					plain ? { ...serverPages, ...stored.pages } : stored.pages,
					page,
					urlPage
				);
			return loaded ?? (plain ? fromServer(page, urlPage) : null);
		},
		write({ state, hits }) {
			const entry = { state: stateKey(state), pages: hits };
			const others = readStored<THit>(key).filter(
				(stored) => stored.state !== entry.state
			);
			writeStored(key, [entry, ...others].slice(0, STATES_KEPT));
		}
	};
}
